import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { AmortizationCalculator } from "../components/AmortizationCalculator";
import { useI18n } from "../context/I18nContext";
import { useAuth, api } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { PiggyBank, ChartLineUp, HandCoins, ArrowRight, Leaf, Trophy, Ticket, Gift, Quotes, UserPlus, Question, WhatsappLogo, Star } from "@phosphor-icons/react";
import { money } from "../lib/format";
import { PowerballRow } from "../components/RaffleTickets";
import { InstallPWA } from "../components/InstallPWA";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ReferralCalculator } from "../components/ReferralCalculator";

const HERO_IMG = "https://images.unsplash.com/photo-1758519288905-38b7b00c1023?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxwZW9wbGUlMjBzbWlsaW5nJTIwaGFuZHNoYWtlJTIwbWVldGluZ3xlbnwwfHx8fDE3ODU5NDM5NjZ8MA&ixlib=rb-4.1.0&q=85";
const GROW_IMG = "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxncmVlbiUyMHBsYW50JTIwZ3Jvd2luZyUyMGNvaW5zfGVufDB8fHx8MTc4NTk0Mzk3N3ww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rates, setRates] = useState({ savings_rate: 0, shares_rate: 0 });
  const [news, setNews] = useState([]);
  const [winners, setWinners] = useState([]);
  const [jackpot, setJackpot] = useState(null);
  const [team, setTeam] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faq, setFaq] = useState([]);
  const [siteConfig, setSiteConfig] = useState(null);
  const [refCfg, setRefCfg] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
    api.get("/raffle/public-winners").then((r) => setWinners(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/raffle/public-jackpot").then((r) => setJackpot(r.data && r.data.enabled ? r.data : null)).catch(() => {});
    api.get("/landing").then((r) => {
      const blocks = Array.isArray(r.data) ? r.data.filter((b) => b.enabled !== false && (b.title || b.text || b.image_url)) : [];
      setNews(blocks);
    }).catch(() => {});
    api.get("/team").then((r) => setTeam(r.data.members || [])).catch(() => {});
    api.get("/referral/public-config").then((r) => setRefCfg(r.data && r.data.active ? r.data : null)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data.items || [])).catch(() => {});
    api.get("/faq").then((r) => setFaq(r.data.items || [])).catch(() => {});
    api.get("/site-config").then((r) => setSiteConfig(r.data)).catch(() => {});
  }, []);

  const start = () => navigate(user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/register");

  return (
    <div className="App">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#F0FDF4] grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 px-4 py-1.5 text-sm font-semibold mb-6">
              <Leaf size={16} weight="fill" /> {t("tagline")}
            </div>
            <h1 className="font-display font-black text-emerald-950 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {t("hero_title")}
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl">{t("hero_sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-cta-btn" onClick={start} className="bg-emerald-800 hover:bg-emerald-900 h-12 px-6 text-base gap-2 shadow-md transition-transform hover:-translate-y-0.5">
                {t("hero_cta")} <ArrowRight size={18} weight="bold" />
              </Button>
              <Button data-testid="hero-calc-btn" onClick={() => document.getElementById("calc").scrollIntoView({ behavior: "smooth" })} variant="outline" className="h-12 px-6 text-base border-emerald-300 text-emerald-800 hover:bg-emerald-50">
                {t("hero_cta2")}
              </Button>
              <InstallPWA />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <img src={HERO_IMG} alt="GEHI" className="rounded-2xl shadow-2xl w-full h-[360px] object-cover" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-xl border border-gray-100 p-4 flex gap-4">
              <RateBadge label={t("savings_rate")} value={rates.savings_rate} t={t} />
              <div className="w-px bg-gray-200" />
              <RateBadge label={t("shares_rate")} value={rates.shares_rate} t={t} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: PiggyBank, t: t("feat_savings_t"), d: t("feat_savings_d") },
            { icon: ChartLineUp, t: t("feat_shares_t"), d: t("feat_shares_d") },
            { icon: HandCoins, t: t("feat_loans_t"), d: t("feat_loans_d") },
          ].map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-white rounded-xl border border-gray-100 p-7 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all"
            >
              <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-5">
                <f.icon size={26} weight="duotone" className="text-emerald-800" />
              </div>
              <h3 className="font-display text-xl font-bold text-emerald-900 mb-2">{f.t}</h3>
              <p className="text-slate-600">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Rates strip */}
      <section className="bg-emerald-900 grain text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold mb-3">{t("rates_title")}</p>
            <div className="flex gap-10">
              <div>
                <div className="font-mono-num text-5xl font-bold text-emerald-300">{rates.savings_rate}%</div>
                <div className="text-emerald-100 mt-1">{t("savings_rate")} <span className="text-emerald-400 text-sm">/ {t("per_year")}</span></div>
              </div>
              <div>
                <div className="font-mono-num text-5xl font-bold text-emerald-300">{rates.shares_rate}%</div>
                <div className="text-emerald-100 mt-1">{t("shares_rate")} <span className="text-emerald-400 text-sm">/ {t("per_year")}</span></div>
              </div>
            </div>
          </div>
          <img src={GROW_IMG} alt="growth" className="rounded-2xl shadow-2xl w-full h-64 object-cover" />
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display font-black text-emerald-950 text-3xl sm:text-4xl">{t("how_title")}</h2>
            <p className="mt-4 text-lg text-slate-600">{t("how_sub")}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: UserPlus, tt: t("how_s1_t"), d: t("how_s1_d") },
              { icon: PiggyBank, tt: t("how_s2_t"), d: t("how_s2_d") },
              { icon: ChartLineUp, tt: t("how_s3_t"), d: t("how_s3_d") },
              { icon: Trophy, tt: t("how_s4_t"), d: t("how_s4_d") },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="relative rounded-2xl border border-emerald-100 bg-[#F0FDF4] p-6" data-testid={`how-step-${i}`}>
                <div className="absolute -top-3 -left-3 h-8 w-8 rounded-full bg-emerald-800 text-white font-bold flex items-center justify-center font-mono-num text-sm">{i + 1}</div>
                <s.icon size={34} weight="duotone" className="text-emerald-700" />
                <h3 className="mt-4 font-display font-bold text-emerald-900 text-lg">{s.tt}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Invite & Earn */}
      {refCfg && (
        <section className="bg-[#F0FDF4] grain" data-testid="invite-earn-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 px-4 py-1.5 text-sm font-semibold mb-5">
                <Gift size={16} weight="fill" /> {t("invite_earn_title")}
              </div>
              <h2 className="font-display font-black text-emerald-950 text-3xl sm:text-4xl leading-tight">{t("invite_earn_title")}</h2>
              <p className="mt-4 text-lg text-slate-600 max-w-xl">{t("invite_earn_sub")}</p>
              {(() => {
                const pp = Math.max((refCfg.level2_amount || 0) / 2, (refCfg.level3_amount || 0) / 4, (refCfg.level4_amount || 0) / 8);
                return pp > 0 ? (
                  <div className="mt-6 inline-flex items-baseline gap-2 rounded-2xl bg-emerald-900 text-white px-6 py-4 shadow-lg" data-testid="invite-earn-amount">
                    <span className="text-sm text-emerald-200">{t("invite_earn_upto")}</span>
                    <span className="font-mono-num font-black text-3xl text-emerald-300">{money(pp, refCfg.currency)}</span>
                    <span className="text-sm text-emerald-200">{t("invite_earn_per")}</span>
                  </div>
                ) : null;
              })()}
              <div className="mt-8">
                <Button data-testid="invite-earn-cta" onClick={start} className="bg-emerald-800 hover:bg-emerald-900 h-12 px-6 text-base gap-2 shadow-md transition-transform hover:-translate-y-0.5">
                  {t("invite_earn_cta")} <ArrowRight size={18} weight="bold" />
                </Button>
              </div>
            </div>
            <div className="space-y-4">
              {[["1", t("invite_earn_p1")], ["2", t("invite_earn_p2")], ["3", t("invite_earn_p3")]].map(([n, txt]) => (
                <motion.div
                  key={n}
                  initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: Number(n) * 0.08 }}
                  className="flex items-center gap-4 bg-white rounded-2xl border border-emerald-100 shadow-sm p-5"
                  data-testid={`invite-earn-step-${n}`}
                >
                  <div className="h-10 w-10 rounded-full bg-emerald-800 text-white font-bold flex items-center justify-center shrink-0 font-mono-num">{n}</div>
                  <p className="text-emerald-900 font-semibold">{txt}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Referral calculator */}
      {refCfg && (
        <section className="bg-white" data-testid="referral-calculator-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
            <ReferralCalculator cfg={refCfg} onStart={start} />
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="bg-emerald-950 grain relative overflow-hidden" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-display font-black text-white text-3xl sm:text-4xl">{t("testimonials_title")}</h2>
              <p className="mt-4 text-lg text-emerald-200">{t("testimonials_sub")}</p>
              {(() => {
                const rated = testimonials.filter((x) => x.rating > 0);
                if (rated.length === 0) return null;
                const avg = rated.reduce((s, x) => s + x.rating, 0) / rated.length;
                return (
                  <div className="mt-6 inline-flex items-center gap-3 rounded-full bg-white/10 border border-white/15 px-5 py-2.5" data-testid="rating-band">
                    <div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={20} weight={n <= Math.round(avg) ? "fill" : "regular"} className="text-amber-400" />)}</div>
                    <span className="font-mono-num font-black text-2xl text-white">{avg.toFixed(1)}</span>
                    <span className="text-emerald-200 text-sm">{t("rating_based_on").replace("{n}", rated.length)}</span>
                  </div>
                );
              })()}
            </div>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((m, i) => {
                const photo = m.photo_url ? `${BACKEND}${m.photo_url}` : null;
                const initials = (m.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-6" data-testid={`testimonial-card-${i}`}>
                    <Quotes size={30} weight="fill" className="text-emerald-400" />
                    <p className="mt-3 text-emerald-50 leading-relaxed">“{m.quote}”</p>
                    <div className="mt-5 flex items-center gap-3">
                      {photo ? <img src={photo} alt="" className="h-11 w-11 rounded-full object-cover border border-emerald-400/40" /> : <div className="h-11 w-11 rounded-full bg-emerald-700 text-white flex items-center justify-center font-bold">{initials}</div>}
                      <div>
                        <div className="text-white font-semibold text-sm">{m.name}</div>
                        {m.role && <div className="text-emerald-300 text-xs">{m.role}</div>}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* News & Announcements (admin-editable) */}
      {news.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="news-section">
          <h2 className="font-display text-3xl font-bold text-emerald-900 mb-8">{t("news_title")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.map((b, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col"
                data-testid={`news-card-${i}`}
              >
                {b.image_url && (
                  <div className="aspect-[16/9] w-full overflow-hidden bg-emerald-50">
                    <img src={`${BACKEND}${b.image_url}`} alt={b.title || "noticia"} className="h-full w-full object-cover" data-testid={`news-img-${i}`} />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  {b.title && <h3 className="font-display font-bold text-emerald-900 text-xl mb-2" data-testid={`news-title-${i}`}>{b.title}</h3>}
                  {b.text && <p className="text-slate-600 leading-relaxed whitespace-pre-line flex-1" data-testid={`news-text-${i}`}>{b.text}</p>}
                  {b.button_text && b.button_link && (
                    <a
                      href={b.button_link} target="_blank" rel="noopener noreferrer"
                      data-testid={`news-btn-${i}`}
                      className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 transition-colors"
                    >
                      {b.button_text}
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </section>
      )}

      {jackpot && jackpot.amount > 0 && (
        <section className="bg-emerald-950 grain" data-testid="jackpot-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative overflow-hidden rounded-3xl border border-amber-400/30 bg-gradient-to-br from-emerald-900 to-emerald-800 px-6 py-10 sm:px-12 sm:py-12 text-center shadow-2xl"
            >
              <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-amber-400/20 blur-3xl" />
              <div className="relative flex items-center justify-center gap-2 mb-3">
                <Trophy size={30} weight="duotone" className="text-amber-400" />
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-amber-300">{t("jackpot_title")}</h2>
              </div>
              <p className="relative text-emerald-100/80 max-w-xl mx-auto mb-6 text-sm sm:text-base">{t("jackpot_sub")}</p>
              <div className="relative font-mono-num font-black text-white text-3xl sm:text-6xl lg:text-7xl tracking-tight leading-tight break-words drop-shadow-lg" data-testid="jackpot-amount">
                {money(jackpot.amount, jackpot.currency)}
              </div>
              {jackpot.rollover_weeks > 0 && (
                <p className="relative mt-3 inline-block rounded-full bg-amber-400/20 px-4 py-1 text-amber-200 text-sm font-semibold" data-testid="jackpot-rollover">
                  {t("jackpot_accumulated").replace("{weeks}", jackpot.rollover_weeks)}
                </p>
              )}
              {(() => {
                if (jackpot.drawn) {
                  return <p className="relative mt-6 text-lg font-bold text-emerald-300" data-testid="jackpot-countdown-done">{t("jackpot_drawn_done")}</p>;
                }
                const target = new Date((jackpot.draw_at || (jackpot.week_end + "T21:00:00")) + "-04:00").getTime();
                const diff = target - nowTs;
                if (diff <= 0) {
                  return <p className="relative mt-6 text-lg font-bold text-amber-300 animate-pulse" data-testid="jackpot-countdown-today">{t("jackpot_today")}</p>;
                }
                const dd = Math.floor(diff / 86400000);
                const hh = Math.floor((diff % 86400000) / 3600000);
                const mm = Math.floor((diff % 3600000) / 60000);
                const ss = Math.floor((diff % 60000) / 1000);
                const box = (val, lbl) => (
                  <div className="flex flex-col items-center">
                    <span className="font-mono-num text-2xl sm:text-4xl font-black text-white bg-white/10 rounded-xl px-3 py-2 min-w-[3rem]">{String(val).padStart(2, "0")}</span>
                    <span className="mt-1 text-[10px] sm:text-xs uppercase tracking-wide text-emerald-200/70">{lbl}</span>
                  </div>
                );
                return (
                  <div className="relative mt-6" data-testid="jackpot-countdown">
                    <p className="text-emerald-200/80 text-sm mb-3">{t("jackpot_draw_in")}</p>
                    <div className="flex items-center justify-center gap-2 sm:gap-3">
                      {box(dd, t("cd_days"))}
                      {box(hh, t("cd_hours"))}
                      {box(mm, t("cd_min"))}
                      {box(ss, t("cd_sec"))}
                    </div>
                  </div>
                );
              })()}
              {jackpot.tickets > 0 && (() => {
                const goal = Math.max(50, Math.ceil((jackpot.tickets + 1) / 50) * 50);
                const pct = Math.min(100, Math.round((jackpot.tickets / goal) * 100));
                return (
                  <div className="relative mt-8 max-w-lg mx-auto" data-testid="jackpot-thermometer">
                    <div className="flex items-center justify-between text-sm text-emerald-100/80 mb-2">
                      <span className="flex items-center gap-1.5"><Ticket size={16} weight="fill" className="text-amber-300" /> <span data-testid="jackpot-tickets-count" className="font-bold text-white">{jackpot.tickets.toLocaleString()}</span> {t("thermo_tickets")}</span>
                      {jackpot.participants > 0 && <span>{jackpot.participants.toLocaleString()} {t("thermo_participants")}</span>}
                    </div>
                    <div className="h-3 w-full rounded-full bg-white/10 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-300"
                      />
                    </div>
                    <p className="mt-2 text-xs text-emerald-200/70">{t("thermo_hint")}</p>
                  </div>
                );
              })()}
              <div className="relative mt-8">
                <Button
                  onClick={start}
                  data-testid="jackpot-play-btn"
                  className="rounded-full bg-amber-400 text-emerald-950 hover:bg-amber-300 font-bold px-8 py-6 text-base"
                >
                  {t("jackpot_play")} <ArrowRight size={18} className="ml-1" />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {winners.length > 0 && (
        <section className="bg-[#F0FDF4] grain" data-testid="winners-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={28} weight="duotone" className="text-amber-500" />
              <h2 className="font-display text-3xl font-bold text-emerald-900">{t("winners_title")}</h2>
            </div>
            <p className="text-slate-600 mb-8">{t("winners_sub")}</p>
            <div className="mb-6 overflow-hidden rounded-xl bg-emerald-900 text-white px-4 py-3" data-testid="winners-banner">
              <p className="font-display text-lg font-semibold animate-pulse">🎉 {winners[0].winner_name} {t("winners_banner_won")} {money(winners[0].prize_amount, winners[0].prize_currency)}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {winners.map((w) => (
                <div key={w.week_start} className="bg-white rounded-xl border border-emerald-100 shadow-sm p-5" data-testid={`winner-${w.week_start}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-emerald-900 flex items-center gap-1.5"><Trophy size={18} weight="fill" className="text-amber-500" /> {w.winner_name}</span>
                    <span className="font-mono-num font-bold text-emerald-700">{money(w.prize_amount, w.prize_currency)}</span>
                  </div>
                  <PowerballRow white={w.white} red={w.red} />
                  <p className="text-xs text-slate-400 mt-3">{t("winners_week")}: {w.week_start} → {w.week_end}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Calculator */}
      <section id="calc" className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <AmortizationCalculator />
      </section>

      {team.length > 0 && (
        <section className="bg-[#F0FDF4] grain" data-testid="landing-board">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-emerald-900">{t("our_board")}</h2>
              <p className="text-slate-500 mt-2 max-w-2xl mx-auto">{t("our_board_sub")}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {team.map((m, i) => {
                const initials = (m.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
                const photo = m.photo_url ? `${BACKEND}${m.photo_url}` : null;
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-6 flex flex-col items-center text-center" data-testid={`board-member-${i}`}>
                    {photo ? <img src={photo} alt={m.name} className="h-28 w-28 rounded-full object-cover border-4 border-emerald-50" /> : <div className="h-28 w-28 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-3xl">{initials || "?"}</div>}
                    <p className="font-display font-bold text-emerald-900 text-lg mt-4">{m.name}</p>
                    {m.role && <p className="text-sm text-emerald-600 font-semibold mt-1">{m.role}</p>}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="bg-white" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 px-4 py-1.5 text-sm font-semibold mb-4"><Question size={16} weight="fill" /> FAQ</div>
              <h2 className="font-display font-black text-emerald-950 text-3xl sm:text-4xl">{t("faq_title")}</h2>
              <p className="mt-4 text-lg text-slate-600">{t("faq_sub")}</p>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {faq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-emerald-100 rounded-xl px-5 bg-[#F0FDF4]/50" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold text-emerald-900 hover:no-underline">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      <footer className="border-t border-gray-200 py-8 text-center text-sm text-slate-500">
        <div className="flex items-center justify-center gap-2 mb-2">
          <img src="/logo.png" className="h-6 w-6" alt="logo" />
          <span className="font-display font-bold text-emerald-900">{t("brand")}</span>
        </div>
        © {new Date().getFullYear()} {t("brand")} — {t("tagline")}
      </footer>

      {siteConfig?.whatsapp && (
        <a
          href={`https://wa.me/${siteConfig.whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(siteConfig.whatsapp_message || t("whatsapp_default_msg"))}`}
          target="_blank" rel="noopener noreferrer"
          data-testid="whatsapp-float-btn"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-4 pr-5 py-3 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all font-semibold"
          aria-label={t("whatsapp_float")}
        >
          <WhatsappLogo size={26} weight="fill" /> <span className="hidden sm:inline">{t("whatsapp_float")}</span>
        </a>
      )}
    </div>
  );
}

const RateBadge = ({ label, value, t }) => (
  <div>
    <div className="font-mono-num text-2xl font-bold text-emerald-700">{value}%</div>
    <div className="text-[11px] text-slate-500 leading-tight">{label}<br />{t("per_year")}</div>
  </div>
);
